import Cookies from 'js-cookie';

const isHttpsProduction = process.env.NODE_ENV === 'production' && 
  (window.location.protocol === 'https:' || window.location.hostname !== 'localhost');

const COOKIE_CONFIG = {
  secure: isHttpsProduction,
  sameSite: 'lax' as const,
  expires: 7,
  path: '/',
};

export const TOKEN_KEYS = {
  AUTH_TOKEN: 'motovolt_auth_token',
  REFRESH_TOKEN: 'motovolt_refresh_token',
  USER_DATA: 'motovolt_user_data',
  HUB_ID: 'motovolt_hub_id',
  HUB_NAME: 'motovolt_hub_name',
  ROLE_HUB_RELATIONS: 'motovolt_role_hub_relations',
} as const;

export interface RoleHubRelation {
  rolehubrelationid: number;
  userid: number;
  roleid: number;
  hubid: number;
}

export const setAuthToken = (token: string): void => {
  try {
    Cookies.set(TOKEN_KEYS.AUTH_TOKEN, token, COOKIE_CONFIG);
    
    localStorage.setItem(TOKEN_KEYS.AUTH_TOKEN, token);
  } catch (error) {
    console.error('Failed to set auth token:', error);
  }
};

export const getAuthToken = (): string | null => {
  try {
    let token = Cookies.get(TOKEN_KEYS.AUTH_TOKEN) || null;
    if (!token) {
      token = localStorage.getItem(TOKEN_KEYS.AUTH_TOKEN) || null;
    }
    return token;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
};

export const setRefreshToken = (token: string): void => {
  try {
    Cookies.set(TOKEN_KEYS.REFRESH_TOKEN, token, COOKIE_CONFIG);
  } catch (error) {
    console.error('Failed to set refresh token:', error);
  }
};

export const getRefreshToken = (): string | null => {
  try {
    return Cookies.get(TOKEN_KEYS.REFRESH_TOKEN) || null;
  } catch (error) {
    console.error('Failed to get refresh token:', error);
    return null;
  }
};

export const setUserData = (userData: any): void => {
  try {
    const userDataString = JSON.stringify(userData);
    Cookies.set(TOKEN_KEYS.USER_DATA, userDataString, COOKIE_CONFIG);
    localStorage.setItem(TOKEN_KEYS.USER_DATA, userDataString);
  } catch (error) {
    console.error('Failed to set user data:', error);
  }
};

export const getUserData = (): any | null => {
  try {
    let userData = Cookies.get(TOKEN_KEYS.USER_DATA);
    if (!userData) {
      userData = localStorage.getItem(TOKEN_KEYS.USER_DATA) || undefined;
    }
    
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Failed to get user data:', error);
    return null;
  }
};

export const setHubId = (hubId: number): void => {
  try {
    localStorage.setItem(TOKEN_KEYS.HUB_ID, hubId.toString());
  } catch (error) {
    console.error('Failed to set hub ID:', error);
  }
};

export const getHubId = (): number | null => {
  try {
    const hubId = localStorage.getItem(TOKEN_KEYS.HUB_ID);
    return hubId ? parseInt(hubId, 10) : null;
  } catch (error) {
    console.error('Failed to get hub ID:', error);
    return null;
  }
};

export const setHubName = (hubName: string): void => {
  try {
    localStorage.setItem(TOKEN_KEYS.HUB_NAME, hubName);
  } catch (error) {
    console.error('Failed to set hub name:', error);
  }
};

export const getHubName = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEYS.HUB_NAME);
  } catch (error) {
    console.error('Failed to get hub name:', error);
    return null;
  }
};

export const setRoleHubRelations = (relations: RoleHubRelation[]): void => {
  try {
    localStorage.setItem(TOKEN_KEYS.ROLE_HUB_RELATIONS, JSON.stringify(relations));
  } catch (error) {
    console.error('Failed to set role-hub relations:', error);
  }
};

export const getRoleHubRelations = (): RoleHubRelation[] | null => {
  try {
    const raw = localStorage.getItem(TOKEN_KEYS.ROLE_HUB_RELATIONS);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.error('Failed to get role-hub relations:', error);
    return null;
  }
};

export const clearAuthCookies = (): void => {
  try {
    Cookies.remove(TOKEN_KEYS.AUTH_TOKEN, { path: '/' });
    Cookies.remove(TOKEN_KEYS.REFRESH_TOKEN, { path: '/' });
    Cookies.remove(TOKEN_KEYS.USER_DATA, { path: '/' });
    localStorage.removeItem(TOKEN_KEYS.AUTH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.USER_DATA);
    localStorage.removeItem(TOKEN_KEYS.HUB_ID);
    localStorage.removeItem(TOKEN_KEYS.HUB_NAME);
    localStorage.removeItem(TOKEN_KEYS.ROLE_HUB_RELATIONS);
  } catch (error) {
    console.error('Failed to clear auth cookies:', error);
  }
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};
